import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Trophy, Flame, Lightning, Crown, Medal, Sparkle } from "@phosphor-icons/react";
import { api } from "@/lib/api";

export default function Play() {
  const [tab, setTab] = useState("missions");
  const [board, setBoard] = useState([]);
  const [quests, setQuests] = useState([]);

  useEffect(() => {
    api.get("/leaderboard").then((r) => setBoard(r.data));
    api.get("/quests").then((r) => setQuests(r.data));
  }, []);

  return (
    <div className="min-h-screen mesh-bg pb-32" data-testid="play-screen">
      <div className="px-6 pt-12 pb-4">
        <p className="text-xs tracking-widest uppercase text-zinc-500">Arena</p>
        <h1 className="font-display text-4xl font-light tracking-tight">Level up.</h1>
      </div>

      {/* Hero stat */}
      <div className="px-6">
        <div className="glass-strong rounded-3xl p-6 relative overflow-hidden">
          <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full bg-amber-300/10 blur-3xl" />
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Tier</div>
              <div className="font-display text-3xl font-light flex items-center gap-2">
                Diamond <Crown size={20} weight="fill" className="text-amber-300" />
              </div>
            </div>
            <Stat icon={<Flame size={14} weight="fill" className="text-amber-300" />} label="Streak" value="41d" />
            <Stat icon={<Lightning size={14} weight="fill" className="text-violet-300" />} label="XP" value="12,810" />
          </div>
          <div className="mt-5 flex items-center gap-3">
            <RingProgress label="Founder" value={72} color="#818CF8" />
            <RingProgress label="AI Skill" value={64} color="#60A5FA" />
            <RingProgress label="Story" value={48} color="#A78BFA" />
            <RingProgress label="Money" value={55} color="#FBBF24" />
          </div>
        </div>
      </div>

      <div className="mt-6 px-6 flex gap-1 glass rounded-full p-1">
        {["missions", "leaderboard", "unlocks"].map((t) => (
          <button
            key={t}
            data-testid={`play-tab-${t}`}
            onClick={() => setTab(t)}
            className={`flex-1 py-2 rounded-full text-xs uppercase tracking-widest transition ${
              tab === t ? "bg-white text-black" : "text-zinc-400"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="mt-5 px-6">
        {tab === "missions" && (
          <motion.div initial="initial" animate="animate" className="space-y-2">
            {quests.map((q, i) => (
              <motion.div
                key={q.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}
                className="glass rounded-2xl p-4 flex items-center gap-3"
                data-testid={`mission-${q.id}`}
              >
                <div className="w-11 h-11 rounded-2xl bg-white/5 flex items-center justify-center">
                  <Sparkle size={18} weight="fill" className="text-violet-300" />
                </div>
                <div className="flex-1">
                  <div className="text-sm font-medium">{q.title}</div>
                  <div className="text-[11px] text-zinc-500">{q.category} mission</div>
                </div>
                <div className="text-xs font-mono-jb text-amber-300">+{q.xp} XP</div>
              </motion.div>
            ))}
          </motion.div>
        )}

        {tab === "leaderboard" && (
          <div className="space-y-2">
            {board.map((p) => (
              <div
                key={p.rank}
                data-testid={`board-${p.rank}`}
                className={`rounded-2xl p-4 flex items-center gap-3 ${p.is_you ? "glass-strong border border-white/20" : "glass"}`}
              >
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center text-sm font-display font-medium ${p.rank === 1 ? "bg-amber-300/20 text-amber-200" : "bg-white/5 text-zinc-300"}`}>
                  {p.rank === 1 ? <Crown size={16} weight="fill" /> : p.rank === 2 ? <Medal size={16} weight="fill" /> : p.rank}
                </div>
                <div className="flex-1">
                  <div className="text-sm font-medium">{p.name}</div>
                  <div className="text-[11px] text-zinc-500">{p.tier} · {p.streak}d streak</div>
                </div>
                <div className="text-xs font-mono-jb text-zinc-300">{p.xp.toLocaleString()}</div>
              </div>
            ))}
          </div>
        )}

        {tab === "unlocks" && (
          <div className="grid grid-cols-2 gap-3">
            {[
              { name: "AI Cofounder", req: "1500 XP", unlocked: true, color: "#818CF8" },
              { name: "Voice Mentor", req: "2000 XP", unlocked: true, color: "#60A5FA" },
              { name: "Pitch Deck Pro", req: "5000 XP", unlocked: false, color: "#A78BFA" },
              { name: "Mythic Mentor", req: "20000 XP", unlocked: false, color: "#FBBF24" },
            ].map((u) => (
              <div key={u.name} className={`glass rounded-2xl p-4 relative overflow-hidden ${!u.unlocked && "opacity-60"}`}>
                <div className="absolute -bottom-6 -right-6 w-20 h-20 rounded-full blur-2xl" style={{ background: u.color, opacity: 0.18 }} />
                <Trophy size={20} weight="fill" style={{ color: u.color }} />
                <div className="mt-2 text-sm font-medium">{u.name}</div>
                <div className="text-[11px] text-zinc-500">{u.unlocked ? "Unlocked" : `Unlocks at ${u.req}`}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const Stat = ({ icon, label, value }) => (
  <div className="text-right">
    <div className="text-[9px] uppercase tracking-widest text-zinc-500 flex items-center gap-1 justify-end">{icon} {label}</div>
    <div className="font-display text-lg font-light">{value}</div>
  </div>
);

const RingProgress = ({ label, value, color }) => {
  const r = 22;
  const c = 2 * Math.PI * r;
  const off = c * (1 - value / 100);
  return (
    <div className="flex-1 flex flex-col items-center">
      <svg width="60" height="60" viewBox="0 0 60 60" className="-rotate-90">
        <circle cx="30" cy="30" r={r} stroke="rgba(255,255,255,0.06)" strokeWidth="4" fill="none" />
        <circle cx="30" cy="30" r={r} stroke={color} strokeWidth="4" strokeDasharray={c} strokeDashoffset={off} fill="none" strokeLinecap="round" />
      </svg>
      <div className="-mt-9 text-xs font-display text-white">{value}</div>
      <div className="mt-3 text-[9px] uppercase tracking-widest text-zinc-500">{label}</div>
    </div>
  );
};
